import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  IFilterData,
  IFilterDataArray,
} from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { Button } from 'react-bootstrap'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import AddSegmentsModal from 'components/configuration/components/lms-profiles/AddSegmentsModal'
import { SegmentsActions } from 'pages/configuration/LmsProfiles/components/segments'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { prefixSegments } from 'pages/configuration/LmsProfiles/constants'
import { Segment } from 'models/segment.model'
import {
  ListSegmentsParams,
  listSegments,
} from 'api/administration/segments/list'
import { SortValue } from 'models/rankmiTable.model'
import SegmentsTable from 'components/configuration/components/lms-profiles/SegmentsTable'
import { canSkipOnRowSelected } from 'helpers'
import { useEditProfile } from '../../../../context'
import { useActions } from './hooks/useActions'
import { Wrapper, HeaderActions } from './styled'

export const Segments = () => {
  const [open, setOpen] = useState(false)

  const [selected, setSelected] = useState<number[]>([])
  const [allSelected, setAllSelected] = useState(false)

  const {
    profile,
    segmentsAdded,
    setSegmentsAdded,
    removedSegments,
    setRemovedSegments,
  } = useEditProfile()
  const [filterData, setFilterData] = useState<IFilterData>()
  const [search, setSearch] = useState('')

  const { loading: loadingActions } = useActions({
    allSelected,
    filterData,
    search,
  })

  const [sort, setSort] = useState<SortValue>()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<Segment[]>([])
  const [total, setTotal] = useState(0)

  const filter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'lms_profiles_id',
        condition: 'and',
        operator: 'eq',
        // @ts-ignore TODO: fix this in @rankmi/rankmi-table
        compareTo: profile?.id,
      },
    ],
    [profile]
  )

  const filterDataMerge = useMemo(
    () => ({
      filterType: 'simple',
      ...filterData,
      data: [...(filterData?.data || []), ...(filter || [])],
    }),
    [filterData, filter]
  )

  const params = useMemo(
    (): Omit<ListSegmentsParams, 'page' | 'perPage'> => ({
      searchText: search,
      sort,
      filterData: filterDataMerge,
    }),
    [search, sort, filterDataMerge]
  )

  const fetch = useCallback(() => {
    setLoading(true)
    listSegments({
      page,
      perPage,
      ...params,
    })
      .then(({ data, total }) => {
        const newData = data.filter(
          (segment) => !removedSegments.includes(segment.id)
        )
        setList(page === 1 ? [...segmentsAdded, ...newData] : newData)
        setTotal(total - removedSegments.length + segmentsAdded.length)
      })
      .finally(() => setLoading(false))
  }, [page, params, perPage])

  useEffect(() => {
    fetch()
  }, [fetch])

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const onAdd = (segments: Segment[]) => {
    setSegmentsAdded((prev) => [...prev, ...segments])
    setRemovedSegments((prev) =>
      prev.filter((id) => !segments.map((segment) => segment.id).includes(id))
    )
    setOpen(false)
    if (page === 1) {
      setList((prev) => [...segments, ...prev])
      setTotal((prev) => prev + segments.length)
    } else {
      setPage(1)
    }
  }

  const onRemove = (segments: number[]) => {
    setRemovedSegments((prev) => [...prev, ...segments])
    setList((prev) => prev.filter((segment) => !segments.includes(segment.id)))
    setTotal((prev) => prev - segments.length)
    setSegmentsAdded((prev) =>
      prev.filter((segment) => !segments.includes(segment.id))
    )
  }

  const addSegmentsFilter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'lms_profiles_id',
        condition: 'and',
        operator: 'not_in',
        compareTo: profile?.id as IFilterDataArray['compareTo'],
      },
    ],
    [profile?.id]
  )

  const tRelative = useTranslationRankmi({
    prefix: prefixSegments,
  })

  return (
    <div className="mt-3">
      <AddSegmentsModal
        show={open}
        onClose={() => setOpen(false)}
        onConfirm={(newSegments) => onAdd(newSegments)}
        segmentsAdded={segmentsAdded}
        filter={addSegmentsFilter}
        saving={loadingActions.add}
      />

      <Wrapper className="position-relative">
        <SegmentsTable
          data={{
            list,
            total,
            loading,
          }}
          onRemove={(segment) => {
            onRemove([segment.id])
            setSelected((prev) => prev.filter((id) => id !== segment.id))
          }}
          onRowSelected={({
            selectedRowKeys,
            allSelected,
            unselectedRowKeys,
            dispatcher,
          }) => {
            if (canSkipOnRowSelected(dispatcher)) return

            setSelected(allSelected ? unselectedRowKeys : selectedRowKeys)
            setAllSelected(allSelected)
          }}
          selected={selected}
          paginationConfig={{
            setSort,
            page,
            perPage,
            onChangeFilterData: setFilterData,
            setPage,
            setPerPage,
            filterData,
            search,
            onSearch: handleSearch,
          }}
          actions={
            <HeaderActions className="d-flex justify-content-end">
              <Button size="sm" onClick={() => setOpen(true)} className="mr-2">
                <FontAwesomeIcon icon={faPlus} />
                {tRelative('addSegments')}
              </Button>
              <SegmentsActions
                selected={
                  allSelected ? total - selected.length : selected.length
                }
                onRemoveSelected={() => {
                  onRemove(selected)
                  setSelected([])
                }}
              />
            </HeaderActions>
          }
        />
      </Wrapper>
    </div>
  )
}
